import {
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	CircularProgress,
	FormControl,
	FormLabel,
	Input,
	Snackbar,
	Switch,
	Tab,
	TabList,
	TabPanel,
	Tabs,
	Table,
	Textarea,
	Typography,
} from "@mui/joy";
import React from "react";
import { useRole } from "../Contexts/RoleContext";
import { Util } from "../Helpers/Util";

interface RcMessage {
	ringcentral_message_id: string;
	direction: string;
	from_number: string;
	to_number: string;
	body: string;
	status: string;
	date_entered: string;
	sent_by_name?: string;
}

interface RcStatus {
	configured: boolean;
	connected: boolean;
	message: string;
	isEnabled: boolean;
	defaultFromNumber?: string;
	extensionNumber?: string;
	name?: string;
}

export default function RingCentralAdmin() {
	const { isActuallyAdmin } = useRole();
	const [tab, setTab] = React.useState(0);
	const [status, setStatus] = React.useState<RcStatus | null>(null);
	const [messages, setMessages] = React.useState<RcMessage[]>([]);
	const [loading, setLoading] = React.useState(true);
	const [syncing, setSyncing] = React.useState(false);
	const [sending, setSending] = React.useState(false);
	const [search, setSearch] = React.useState("");
	const [isEnabled, setIsEnabled] = React.useState(false);
	const [toNumber, setToNumber] = React.useState("");
	const [smsBody, setSmsBody] = React.useState("");
	const [toast, setToast] = React.useState<string | null>(null);
	const [error, setError] = React.useState<string | null>(null);

	const fromNumber = status?.defaultFromNumber ?? "";

	const loadAll = React.useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const [statusRes, settingsRes, messagesRes] = await Promise.all([
				fetch("/RingCentral/GetStatus", { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" }),
				fetch("/RingCentral/GetSettings", { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" }),
				fetch("/RingCentral/GetMessages", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ top: 200 }) }),
			]);

			const statusJson = await statusRes.json();
			const settingsJson = await settingsRes.json();
			const messagesJson = await messagesRes.json();

			setStatus(statusJson);
			setIsEnabled(!!settingsJson.isEnabled);
			setMessages(Array.isArray(messagesJson) ? messagesJson : []);
			if (!statusJson.defaultFromNumber)
				setError("DefaultFromNumber is not set in appsettings (RingCentral section).");
		} catch (e: any) {
			setError(e?.message ?? "Failed to load RingCentral data");
		} finally {
			setLoading(false);
		}
	}, []);

	React.useEffect(() => {
		if (isActuallyAdmin()) loadAll();
	}, [isActuallyAdmin, loadAll]);

	const saveSettings = async () => {
		const res = await fetch("/RingCentral/SaveSettings", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ isEnabled }),
		});
		const json = await res.json();
		if (json.error) setError(json.error);
		else setToast("Settings saved");
	};

	const syncMessages = async () => {
		setSyncing(true);
		try {
			const res = await fetch("/RingCentral/SyncMessages", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ days: 30 }),
			});
			const json = await res.json();
			if (json.error) setError(json.error);
			else {
				setToast(`Synced ${json.synced ?? 0} messages`);
				const msgRes = await fetch("/RingCentral/GetMessages", {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ top: 200 }),
				});
				setMessages(await msgRes.json());
			}
		} finally {
			setSyncing(false);
		}
	};

	const sendSms = async () => {
		setSending(true);
		setError(null);
		try {
			const res = await fetch("/RingCentral/SendSms", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ to: toNumber, text: smsBody }),
			});
			const json = await res.json();
			if (json.error) setError(json.error);
			else {
				setToast("Message sent");
				setSmsBody("");
				setToNumber("");
				loadAll();
			}
		} finally {
			setSending(false);
		}
	};

	if (!isActuallyAdmin()) {
		return <Alert color="warning">Admin access required.</Alert>;
	}

	const filtered = messages.filter(
		(m) =>
			Util.isNullOrEmpty(search) ||
			m.body?.toLowerCase().includes(search.toLowerCase()) ||
			m.from_number?.includes(search) ||
			m.to_number?.includes(search)
	);

	const canSend = isEnabled && !!fromNumber;

	return (
		<Box sx={{ overflowY: "auto" }}>
			<Typography level="h3" sx={{ mb: 2 }}>
				RingCentral SMS
			</Typography>

			{loading ? (
				<CircularProgress />
			) : (
				<>
					<Card sx={{ mb: 2 }}>
						<CardContent>
							<Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
								<Chip color={status?.connected ? "success" : "danger"} variant="soft">
									{status?.connected ? "Connected" : "Not connected"}
								</Chip>
								<Chip color={status?.configured ? "neutral" : "warning"} variant="outlined">
									{status?.configured ? "Credentials configured" : "Missing credentials"}
								</Chip>
								<Chip color={isEnabled ? "success" : "neutral"} variant="outlined">
									{isEnabled ? "Sending enabled" : "Sending disabled"}
								</Chip>
							</Box>
							<Typography level="body-sm" sx={{ mt: 1 }}>
								{status?.message}
								{status?.name ? ` — ${status.name}` : ""}
								{status?.extensionNumber ? ` (ext ${status.extensionNumber})` : ""}
							</Typography>
							<Typography level="body-sm" sx={{ mt: 0.5 }}>
								Send from: <strong>{fromNumber || "(not configured)"}</strong>
							</Typography>
						</CardContent>
					</Card>

					<Tabs value={tab} onChange={(_, v) => setTab(v as number)}>
						<TabList>
							<Tab>Compose</Tab>
							<Tab>Messages</Tab>
							<Tab>Settings</Tab>
						</TabList>

						<TabPanel value={0}>
							<FormControl sx={{ mb: 2, maxWidth: 320 }}>
								<FormLabel>To</FormLabel>
								<Input placeholder="+1..." value={toNumber} onChange={(e) => setToNumber(e.target.value)} />
							</FormControl>
							<FormControl sx={{ mb: 2 }}>
								<FormLabel>Message</FormLabel>
								<Textarea minRows={4} value={smsBody} onChange={(e) => setSmsBody(e.target.value)} />
							</FormControl>
							<Button loading={sending} disabled={!canSend} onClick={sendSms}>
								Send SMS
							</Button>
							{!isEnabled && (
								<Typography level="body-sm" sx={{ mt: 1 }}>
									Enable sending in Settings first.
								</Typography>
							)}
							{isEnabled && !fromNumber && (
								<Typography level="body-sm" sx={{ mt: 1 }}>
									Set RingCentral:DefaultFromNumber in appsettings.
								</Typography>
							)}
						</TabPanel>

						<TabPanel value={1}>
							<Box sx={{ display: "flex", gap: 1, mb: 2 }}>
								<Input placeholder="Search messages..." value={search} onChange={(e) => setSearch(e.target.value)} />
								<Button loading={syncing} variant="outlined" onClick={syncMessages}>
									Sync from RingCentral
								</Button>
							</Box>
							<Table stripe="even" hoverRow>
								<thead>
									<tr>
										<th>When</th>
										<th>Dir</th>
										<th>From</th>
										<th>To</th>
										<th>Body</th>
										<th>Status</th>
									</tr>
								</thead>
								<tbody>
									{filtered.map((m) => (
										<tr key={m.ringcentral_message_id}>
											<td>{new Date(m.date_entered).toLocaleString()}</td>
											<td>{m.direction}</td>
											<td>{m.from_number}</td>
											<td>{m.to_number}</td>
											<td>{m.body}</td>
											<td>{m.status}</td>
										</tr>
									))}
								</tbody>
							</Table>
						</TabPanel>

						<TabPanel value={2}>
							<FormControl orientation="horizontal" sx={{ mb: 2 }}>
								<FormLabel>Enable outbound SMS</FormLabel>
								<Switch checked={isEnabled} onChange={(e) => setIsEnabled(e.target.checked)} />
							</FormControl>
							<Button onClick={saveSettings}>Save settings</Button>
							<Typography level="body-sm" sx={{ mt: 2 }}>
								Sender number: <strong>{fromNumber || "(not configured)"}</strong> — set in appsettings{" "}
								<code>RingCentral:DefaultFromNumber</code>. API credentials and webhook URL are also configured server-side.
							</Typography>
						</TabPanel>
					</Tabs>
				</>
			)}

			<Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast(null)}>
				{toast}
			</Snackbar>
			<Snackbar open={!!error} color="danger" onClose={() => setError(null)}>
				{error}
			</Snackbar>
		</Box>
	);
}
